import React, { useState, useEffect, useRef, useCallback } from "react";

// Markdown renderer
const renderInlineFormatting = (text, keyPrefix = "") => {
	if (!text) return null;

	const tokens = [];
	let remaining = text;
	let key = 0;

	while (remaining.length > 0) {
		// Check for inline code first
		const codeMatch = remaining.match(/^`([^`]+)`/);
		if (codeMatch) {
			tokens.push(
				<code key={`${keyPrefix}-${key++}`} className="chat-code">
					{codeMatch[1]}
				</code>
			);
			remaining = remaining.slice(codeMatch[0].length);
			continue;
		}

		// Bold **text**
		const boldMatch = remaining.match(/^\*\*([^*]+)\*\*/);
		if (boldMatch) {
			tokens.push(<strong key={`${keyPrefix}-${key++}`}>{boldMatch[1]}</strong>);
			remaining = remaining.slice(boldMatch[0].length);
			continue;
		}

		// Italic *text* or _text_
		const italicMatch = remaining.match(/^(\*|_)([^*_]+)\1/);
		if (italicMatch) {
			tokens.push(<em key={`${keyPrefix}-${key++}`}>{italicMatch[2]}</em>);
			remaining = remaining.slice(italicMatch[0].length);
			continue;
		}

		// Links [text](url)
		const linkMatch = remaining.match(/^\[([^\]]+)\]\(([^)]+)\)/);
		if (linkMatch) {
			tokens.push(
				<a key={`${keyPrefix}-${key++}`} href={linkMatch[2]} target="_blank" rel="noopener noreferrer">
					{linkMatch[1]}
				</a>
			);
			remaining = remaining.slice(linkMatch[0].length);
			continue;
		}

		tokens.push(remaining[0]);
		remaining = remaining.slice(1);
	}

	return tokens;
};

const renderContent = (text) => {
	if (!text) return null;

	// Split by code blocks first
	const parts = text.split(/(```[\s\S]*?```)/g);

	return parts.map((part, i) => {
		// Code blocks
		if (part.startsWith("```")) {
			const match = part.match(/```(\w*)\n?([\s\S]*?)```/);
			if (match) {
				const [, lang, code] = match;
				return (
					<pre key={i} className="chat-code-block" data-lang={lang || undefined}>
						<code>{code.trim()}</code>
					</pre>
				);
			}
		}

		// Process line by line
		const lines = part.split("\n");
		const elements = [];
		let listItems = [];
		let listType = null;

		const flushList = () => {
			if (listItems.length > 0) {
				const ListTag = listType === "ol" ? "ol" : "ul";
				elements.push(
					<ListTag key={`list-${elements.length}`} className="chat-list">
						{listItems}
					</ListTag>
				);
				listItems = [];
				listType = null;
			}
		};

		lines.forEach((line, j) => {
			const trimmed = line.trim();

			// Headers
			const headerMatch = trimmed.match(/^(#{1,3})\s+(.+)$/);
			if (headerMatch) {
				flushList();
				elements.push(
					<strong key={`h-${j}`} className="chat-heading">
						{renderInlineFormatting(headerMatch[2], `h-${j}`)}
					</strong>
				);
				return;
			}

			// Unordered list
			const ulMatch = trimmed.match(/^[-*]\s+(.+)$/);
			if (ulMatch) {
				if (listType !== "ul") flushList();
				listType = "ul";
				listItems.push(
					<li key={`li-${j}`}>{renderInlineFormatting(ulMatch[1], `li-${j}`)}</li>
				);
				return;
			}

			// Ordered list
			const olMatch = trimmed.match(/^\d+\.\s+(.+)$/);
			if (olMatch) {
				if (listType !== "ol") flushList();
				listType = "ol";
				listItems.push(
					<li key={`li-${j}`}>{renderInlineFormatting(olMatch[1], `li-${j}`)}</li>
				);
				return;
			}

			// Regular line
			flushList();
			if (trimmed) {
				elements.push(
					<span key={`line-${j}`}>
						{renderInlineFormatting(line, `line-${j}`)}
						{j < lines.length - 1 && <br />}
					</span>
				);
			} else if (j < lines.length - 1) {
				elements.push(<br key={`br-${j}`} />);
			}
		});

		flushList();
		return <span key={i}>{elements}</span>;
	});
};

const ClaudeChat = ({ isVisible }) => {
	const [messages, setMessages] = useState([]);
	const [input, setInput] = useState("");
	const [isConnected, setIsConnected] = useState(false);
	const [isProcessing, setIsProcessing] = useState(false);
	const [currentResponse, setCurrentResponse] = useState("");
	const [hasStarted, setHasStarted] = useState(false);

	const wsRef = useRef(null);
	const messagesEndRef = useRef(null);

	const scrollToBottom = () => {
		messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
	};

	useEffect(() => {
		scrollToBottom();
	}, [messages, currentResponse]);

	const connect = useCallback(() => {
		// Derive WebSocket URL from backend URL env var
		const backendUrl = process.env.REACT_APP_BACKEND_URL || `http://${window.location.hostname}:8080`;
		const wsProtocol = backendUrl.startsWith('https') ? 'wss' : 'ws';
		const wsHost = backendUrl.replace(/^https?:\/\//, '');
		const wsUrl = `${wsProtocol}://${wsHost}/terminal`;
		const ws = new WebSocket(wsUrl);
		wsRef.current = ws;

		ws.onopen = () => {
			console.log("Claude Chat connected");
			setIsConnected(true);
			setMessages([{
				type: "assistant",
				content: "Good afternoon, Nicholas. How can I help you today?",
			}]);
		};

		ws.onmessage = (event) => {
			try {
				const msg = JSON.parse(event.data);
				console.log("Received:", msg.type, msg);

				switch (msg.type) {
					case "assistant":
						if (msg.message?.content) {
							const text = msg.message.content
								.filter(c => c.type === "text")
								.map(c => c.text)
								.join("");
							if (text) {
								setMessages((prev) => [...prev, {
									type: "assistant",
									content: text,
								}]);
							}
						}
						setCurrentResponse("");
						setIsProcessing(false);
						break;

					case "content_block_start":
						setIsProcessing(true);
						break;

					case "content_block_delta":
						if (msg.delta?.text) {
							setCurrentResponse((prev) => prev + msg.delta.text);
						}
						break;

					case "content_block_stop":
						setCurrentResponse((prev) => {
							if (prev.trim()) {
								setMessages((msgs) => [...msgs, {
									type: "assistant",
									content: prev.trim(),
								}]);
							}
							return "";
						});
						break;

					case "message_start":
						setIsProcessing(true);
						setCurrentResponse("");
						break;

					case "message_stop":
					case "result":
						if (currentResponse.trim()) {
							setMessages((prev) => [...prev, {
								type: "assistant",
								content: currentResponse.trim(),
							}]);
							setCurrentResponse("");
						}
						setIsProcessing(false);
						break;

					case "error":
						setMessages((prev) => [...prev, {
							type: "error",
							content: msg.data || msg.error?.message || "Unknown error",
						}]);
						setIsProcessing(false);
						break;

					case "exit":
						setMessages((prev) => [...prev, {
							type: "system",
							content: `Session ended (code: ${msg.code})`,
						}]);
						setIsConnected(false);
						setIsProcessing(false);
						break;

					case "raw":
						if (msg.data) {
							setCurrentResponse((prev) => prev + msg.data + "\n");
						}
						break;

					default:
						console.log("Unhandled message type:", msg.type, msg);
						break;
				}
			} catch (err) {
				console.error("Message parse error:", err, event.data);
			}
		};

		ws.onclose = () => {
			console.log("Claude Chat disconnected");
			setIsConnected(false);
			setIsProcessing(false);
		};

		ws.onerror = (err) => {
			console.error("WebSocket error:", err);
			setMessages((prev) => [...prev, {
				type: "error",
				content: "Connection error. Is the backend running?",
			}]);
		};

		return ws;
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (hasStarted && !wsRef.current) {
			connect();
		}

		return () => {
			if (wsRef.current) {
				wsRef.current.close();
				wsRef.current = null;
			}
		};
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [hasStarted]);

	const handleStart = () => {
		setHasStarted(true);
	};

	const handleSubmit = (e) => {
		e.preventDefault();
		if (!input.trim() || !isConnected || isProcessing) return;

		setMessages((prev) => [...prev, {
			type: "user",
			content: input.trim(),
		}]);

		wsRef.current?.send(JSON.stringify({
			type: "input",
			data: input.trim(),
		}));

		setInput("");
		setIsProcessing(true);
	};

	const handleInterrupt = () => {
		wsRef.current?.send(JSON.stringify({ type: "interrupt" }));
		setIsProcessing(false);
	};

	const handleKeyDown = (e) => {
		if (e.key === "Enter" && !e.shiftKey) {
			e.preventDefault();
			handleSubmit(e);
		}
	};

	// Startup screen
	if (!hasStarted) {
		return (
			<>
				<div className="chat-overlay__messages chat-overlay__startup">
					<div className="chat-startup">
						<div className="chat-startup__icon">◈</div>
						<h2 className="chat-startup__title">Lazlo</h2>
						<p className="chat-startup__desc">
							Your VA assistant, ready to help with tasks, calendar, and more.
						</p>
						<button className="chat-startup__btn" onClick={handleStart}>
							Start Session
						</button>
					</div>
				</div>
			</>
		);
	}

	return (
		<>
			<div className="chat-overlay__messages">
				{messages.map((msg, i) => (
					<div key={i} className={`chat-message chat-message--${msg.type}`}>
						<div className="chat-message__content">
							{renderContent(msg.content)}
						</div>
					</div>
				))}

				{/* Streaming response */}
				{currentResponse && (
					<div className="chat-message chat-message--assistant chat-message--streaming">
						<div className="chat-message__content">
							{renderContent(currentResponse)}
							<span className="chat-cursor" />
						</div>
					</div>
				)}

				{isProcessing && !currentResponse && (
					<div className="chat-message chat-message--assistant chat-message--thinking">
						<div className="chat-message__content">
							<span className="chat-thinking">
								<span>.</span><span>.</span><span>.</span>
							</span>
						</div>
					</div>
				)}

				<div ref={messagesEndRef} />
			</div>

			<div className="chat-overlay__input">
				<input
					type="text"
					value={input}
					onChange={(e) => setInput(e.target.value)}
					onKeyDown={handleKeyDown}
					placeholder={isConnected ? "Ask Lazlo anything..." : "Connecting..."}
					disabled={!isConnected}
				/>
				{isProcessing ? (
					<button
						type="button"
						className="chat-overlay__send chat-overlay__send--stop"
						onClick={handleInterrupt}
						title="Stop"
					>
						■
					</button>
				) : (
					<button
						type="button"
						className="chat-overlay__send"
						onClick={handleSubmit}
						disabled={!input.trim() || !isConnected}
						title="Send"
					>
						→
					</button>
				)}
			</div>
		</>
	);
};

export default ClaudeChat;
